import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  constructSignInQueryString, addSigninSuccessListener,
} from '../../../../components/Auth';
import {
  AppRunOptions,
  useAppRun,
} from '../../../../hooks/useAppRun';
import CustomizationProvider from '../../../../components/customization/customization-provider';
import {
  StylingWithDefaults,
} from '../../../../types/ClientState';
import {
  TPalette,
  TFont,
  TLayout,
} from '../../../../../generated/gql/graphql';
import {
  useClientStore,
} from '../../../../hooks/ClientState';
import {
  Property,
} from 'csstype';
import {
  Box,
  LinearProgress,
  Stack,
  Theme,
  Button,
  Typography,
  Snackbar,
  Alert,
  Fade,
  Grow,
  Collapse,
} from '@mui/material';
import {
  useApolloClient,
} from '@apollo/client';
import {
  useEditorStore,
} from '../../../../hooks/EditorState';
import {
  createFlowFromZustand,
} from '../../../../utils/graph-conversion';
import {
  useShallow,
} from 'zustand/react/shallow';
import { AppPopover } from './AppPopover';
import { ChatView } from './ChatView';
import { DEV_SERVER_URL, PROD_SERVER_URL } from '../../../../constants/common';
import { initChildAppListener, notifyParentApp } from '../../../../utils/childApp';
import { EmbeddedAppContext } from '../../../../contexts/EmbeddedAppContext';
import { Icon } from '@iconify/react';
import { useCanvas } from '../../../../hooks/useCanvas';
import { CanvasView } from './CanvasView';
import { CanvasPill } from './CanvasPill';

// SigninPrompt component to show when authentication is required
function SigninPrompt({ onSigninClick }: { onSigninClick: () => void }) {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      p: 3
    }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Authentication Required
      </Typography>
      <Typography sx={{ mb: 3, textAlign: 'center' }}>
        Please sign in to use this application.
      </Typography>
      <Button
        variant="contained"
        onClick={onSigninClick}
      >
        Sign In
      </Button>
    </Box>
  );
}

const CHAT_VIEW_WIDTH = '800px';

// SnackbarItem component for displaying status messages with expand/collapse functionality
function SnackbarItem({
  message,
  severity,
  alwaysExpanded = false
}: {
  message: string,
  severity: 'error' | 'warning' | 'info' | 'success',
  alwaysExpanded?: boolean
}) {
  const [expanded, setExpanded] = useState(false);

  // Use either the internal state or the alwaysExpanded prop
  const isExpanded = alwaysExpanded || expanded;

  return (
    <Box
      sx={{
        backgroundColor: theme => theme.palette[severity].main,
        color: theme => theme.palette.getContrastText(theme.palette[severity].main),
        borderRadius: 10,
        boxShadow: theme => theme.shadows[4],
        px: 3,
        py: 1,
        width: '50ch',
        minHeight: '4ch',
        opacity: 0.8,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        pointerEvents: 'auto', // Enable pointer events on the notification
        cursor: 'pointer', // Show pointer cursor to indicate clickable
      }}
      onClick={() => !alwaysExpanded && setExpanded(!expanded)} // Only toggle if not alwaysExpanded
    >
      <Typography
        variant='body2'
        sx={{
          overflow: isExpanded ? 'visible' : 'hidden',
          textOverflow: isExpanded ? 'clip' : 'ellipsis',
          whiteSpace: isExpanded ? 'normal' : 'nowrap',
          width: '100%',
          textAlign: 'center'
        }}
      >
        <b>{message}</b>
      </Typography>
    </Box>
  );
}

// NOTE this component needs to be inside a GraphQLProvider and AppContainer
// TODO this should be the designated component to manage client state, right now it's all over the place
export function AppClientInPopover(props: {
  flowId: string | undefined,
  authToken?: string;
  palette?: TPalette;
  font?: TFont;
  layout?: TLayout;
  onStylingLoaded?: (styling: StylingWithDefaults) => void;
  //whether to replay past side effects on initial connection
  replayEffectsOnConnect?: boolean;
  onClose?: () => void;
  initialOpen?: boolean;
  debug?: boolean;
  disconnectOnClose?: boolean;
  // only effective if disconnectOnClose is true. if true, the session will be terminated on close
  quitSessionOnClose?: boolean;
  backdropFilter?: Property.BackdropFilter;
  disableQuickClose?: boolean;
  theme?: Partial<Theme>;
  // set fullWidth to display popover in full width even when it's chat only
  fullWidth?: boolean;
}): React.ReactElement {
  const [started, setStarted] = useState(false);
  const [signinRequired, setSigninRequired] = useState(false);
  const setFlowId = useClientStore(state => state.setFlowId);
  const loadStylingCustomizations = useClientStore(state => state.loadStylingCustomizations);
  const apolloClient = useApolloClient();

  // Add state for snackbar expanded mode
  const [snackbarsExpanded, setSnackbarsExpanded] = useState(false);

  // Add transition state to handle smooth animations
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Enhanced handlers for toggling with transition
  const handleExpandSnackbars = useCallback(() => {
    setIsTransitioning(true);
    setSnackbarsExpanded(true);
  }, []);

  const handleCollapseSnackbars = useCallback(() => {
    setIsTransitioning(true);
    setSnackbarsExpanded(false);
  }, []);

  // Handle transition end
  const handleTransitionEnd = useCallback(() => {
    setIsTransitioning(false);
  }, []);

  useEffect(() => {
    setFlowId(props.flowId);
    loadStylingCustomizations(apolloClient).then(() => {
      const state = useClientStore.getState();
      props.onStylingLoaded?.({
        palette: state.getPalatte(),
        font: state.getFont(),
        layout: state.getLayout(),
        icon: state.icon,
        openAfter: state.openAfter,
        profilePicUrl: state.profilePicUrl,
      });
    });
  }, [props.flowId, apolloClient]);

  const initializing = useClientStore(state => state.initializing);

  const { canvasCount, canvasOpen, handleOpenCanvas } = useCanvas(props.debug);

  const nodesData = useEditorStore(useShallow(state => state.graph.nodes.map(node => node.data)));
  const edges = useEditorStore(useShallow(state => state.graph.edges));
  const [
    startNodeId,
    aiConfig,
  ] = useEditorStore(useShallow(state => [
    state.app.startNodeId,
    state.app.aiConfig,
  ]));

  const disconnectRef = React.useRef(() => { });
  const appRunOptions = useMemo<AppRunOptions>(() => {
    if (!props.flowId) {
      return {
        debug: false,
        flowId: props.flowId,
        startId: null,
      };
    }
    if (props.debug) {
      const flow = createFlowFromZustand(nodesData, edges);
      return {
        debug: true,
        flowConfig: flow,
        flowId: props.flowId,
        startId: startNodeId,
        aiConfig,
      };
    }
    return {
      debug: false,
      flowId: props.flowId,
      startId: null,
    };
  }, [props.flowId, props.debug, nodesData, edges, startNodeId, aiConfig]);

  const handleSigninRequest = useCallback(() => {
    console.log('signin required');
    setSigninRequired(true);
  }, []);

  const appRun = useAppRun(
    appRunOptions,
    handleSigninRequest,
  );

  const handleSigninClick = useCallback(() => {
    const isDev = window.location.href.startsWith(DEV_SERVER_URL);
    const baseUrl = isDev ? DEV_SERVER_URL : PROD_SERVER_URL;
    const queryString = constructSignInQueryString({
      mode: 'popup',
      enableSignup: true,
      message: 'Please signin first to use the app.',
    });
    const signInUrl = `${baseUrl}/signin?${queryString}`;
    const unregister = addSigninSuccessListener(baseUrl, () => {
      setSigninRequired(false);
      // NOTE: this could be wrong if signin required is not triggered at the beginning
      appRun.send([]);
    });
    window.open(signInUrl, "popup", "width=800,height=600,location=no,menubar=no,toolbar=no");

    return unregister;
  }, [appRun]);

  useEffect(() => {
    disconnectRef.current = appRun.disconnect;
  }, [appRun.disconnect]);

  const onPopoverOpen = useCallback(() => {
    if (!started) {
      setStarted(true);
      appRun.start(props.replayEffectsOnConnect);
    }
  }, [started, appRun.start, props.replayEffectsOnConnect]);

  const onPopoverClose = useCallback(() => {
    if (props.disconnectOnClose) {
      appRun.disconnect(!props.quitSessionOnClose);
    }
    props.onClose?.();
  }, [appRun.disconnect, props.onClose, props.quitSessionOnClose]);

  const setCurrentBreakpoint = useEditorStore(state => state.actions.setCurrentBreakpoint);
  const currentBreakpoint = useEditorStore(useShallow(state => state.app?.currentBreakpoint));
  const rerunFromNodeAfterBreakpoint = useEditorStore(useShallow(state => state.app?.rerunFromNodeAfterBreakpoint));
  const rerunFromNodeAfterBreakpointRef = React.useRef(rerunFromNodeAfterBreakpoint);
  const isInterruptedByError = useClientStore(state => state.interrupted === 'error');

  useEffect(() => {
    rerunFromNodeAfterBreakpointRef.current = rerunFromNodeAfterBreakpoint;
  }, [rerunFromNodeAfterBreakpoint?.nodeId, rerunFromNodeAfterBreakpoint?.nodeVersion]);

  // automatically rerun the app from the configured node when a breakpoint is hit
  // this is for repeating part of the flow for ai learning
  useEffect(() => {
    const rerunFrom = rerunFromNodeAfterBreakpointRef.current;
    if (rerunFrom && (currentBreakpoint || isInterruptedByError)) {
      setCurrentBreakpoint(null);
      appRun.rerun(rerunFrom.nodeId, rerunFrom.nodeVersion);
    }
  }, [currentBreakpoint, isInterruptedByError]);

  const setChildAppState = useClientStore(state => state.setChildAppState);
  useEffect(() => initChildAppListener(setChildAppState), [setChildAppState]);

  const { tabId, parentTabId, isChromeExtension } = useContext(EmbeddedAppContext);

  // Subscribe to client state changes and notify parent app
  useEffect(() => {
    // return useClientStore.subscribe(
    //   state => state.clientId,
    //   console.log,
    // );
    return useClientStore.subscribe(
      state => ({
        clientId: state.clientId || '',
        tabId,
        flowId: state.flowId,
        unreadCount: state.unreadCount,
        serverInProgress: state.serverInProgress,
        completed: state.completed,
        interrupted: state.interrupted
      }),
      (currentState) => {
        if (!currentState.clientId) return;
        notifyParentApp(currentState, parentTabId);
      }
    );
  }, [parentTabId, tabId]);

  const clientId = useClientStore(state => state.clientId);
  useEffect(() => {
    const sub = () => {
      notifyParentApp({ terminated: true, clientId }, parentTabId);
    }
    window.addEventListener('beforeunload', sub);
    return () => {
      window.removeEventListener('beforeunload', sub);
    }
  }, [parentTabId, clientId]);

  const profilePicUrl = useClientStore(state => state.profilePicUrl);
  const appName = useClientStore(state => state.appName);
  useEffect(() => {
    if (appName) {
      document.title = appName;
    }
  }, [appName]);

  const [snackbars, setSnackbars] = useState<{ message: string, severity: 'error' | 'warning' | 'info' | 'success' }[]>([]);

  useEffect(() => {
    const unsubscribe = useClientStore.subscribe(
      state => state.childApps,
      (childApps) => {
        const childAppMessages = Object.entries(childApps).map(([clientId, appState]) => {
          const status = appState.completed
            ? 'done'
            : appState.terminated
              ? 'terminated'
              : appState.interrupted
                ? appState.interrupted === 'stop'
                  ? 'paused'
                  : 'having problems'
                : appState.serverInProgress
                  ? 'working'
                  : 'waiting your input';

          const severity: "error" | "success" | "warning" | "info" = appState.completed
            ? 'success'
            : appState.terminated
              ? 'error'
              : appState.interrupted
                ? appState.interrupted === 'stop'
                  ? 'warning'
                  : 'error'
                : appState.serverInProgress
                  ? 'info'
                  : 'warning';

          return {
            message: `${appState.appName || 'Pixie'} is ${status}`,
            severity,
          };
        });

        setSnackbars(childAppMessages);
      }
    );
    return () => unsubscribe();
  }, []);

  const appRunCommand = useClientStore(state => state.appRunCommand);
  useEffect(() => {
    if (appRunCommand) {
      switch (appRunCommand.type) {
        case 'rerun':
          appRun.rerun(appRunCommand.nodeId, appRunCommand.nodeVersion);
          break;
        default:
          console.warn('Unknown app run command', appRunCommand);
          break;
      }
    }
  }, [appRunCommand]);


  return <CustomizationProvider
    palette={props.palette}
    font={props.font}
    theme={props.theme}
  >
    <AppPopover
      authToken={props.authToken}
      showLoading={!props.flowId || !appRun.isReady}
      initialOpen={props.initialOpen}
      onOpen={onPopoverOpen}
      onClose={onPopoverClose}
      backdropFilter={props.backdropFilter}
      disableQuickClose={props.disableQuickClose}
      layout={props.layout}
      // we don't want extra whitespace when it's only showing chat conversation so set maxWidth to chatView width
      maxWidth={canvasOpen || props.fullWidth ? undefined : CHAT_VIEW_WIDTH}
    >
      {/* Position relative container to allow absolute positioning within */}
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* Snackbar container positioned absolutely within the AppPopover */}
        {snackbars.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1400,
              width: '100%',
              pointerEvents: 'none', // Allow clicking through
            }}
          >
            <Stack spacing={1} alignItems="center">
              {/* Show "Show less" button at the top when expanded */}
              <Fade in={snackbarsExpanded && snackbars.length > 1} timeout={300} mountOnEnter unmountOnExit>
                <Box
                  sx={{
                    pointerEvents: 'auto',
                    cursor: 'pointer',
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    color: 'white',
                    borderRadius: 10,
                    px: 2,
                    py: 0.5,
                    mb: 1,
                    display: snackbarsExpanded && snackbars.length > 1 ? 'block' : 'none',
                  }}
                  onClick={handleCollapseSnackbars}
                  onTransitionEnd={handleTransitionEnd}
                >
                  <Typography variant="caption">Show less</Typography>
                </Box>
              </Fade>

              {/* In compact mode, show only the last notification */}
              {(!snackbarsExpanded && snackbars.length > 0) ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.3s ease-in-out',
                  }}
                  onTransitionEnd={handleTransitionEnd}
                >
                  <SnackbarItem
                    message={snackbars[snackbars.length - 1].message}
                    severity={snackbars[snackbars.length - 1].severity}
                  />
                  {/* Show "+x more" when there are multiple notifications */}
                  <Grow
                    in={snackbars.length > 1}
                    timeout={300}
                    style={{ transformOrigin: '0 0 0' }}
                  >
                    <Box
                      sx={{
                        ml: 1,
                        pointerEvents: 'auto',
                        cursor: 'pointer',
                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        borderRadius: 10,
                        px: 2,
                        py: 0.5,
                        display: snackbars.length > 1 ? 'block' : 'none',
                      }}
                      onClick={handleExpandSnackbars}
                    >
                      <Typography variant="caption">+{snackbars.length - 1} more</Typography>
                    </Box>
                  </Grow>
                </Box>
              ) : (
                // In expanded mode, show all notifications with staggered animation
                <Collapse
                  in={snackbarsExpanded}
                  timeout={300}
                  onTransitionEnd={handleTransitionEnd}
                >
                  <Stack spacing={1} alignItems="center">
                    {snackbars.map((snackbar, index) => (
                      <Fade
                        in={snackbarsExpanded}
                        timeout={{ enter: 300 + index * 100, exit: 200 }}
                        key={index}
                      >
                        <div>
                          <SnackbarItem
                            message={snackbar.message}
                            severity={snackbar.severity}
                            alwaysExpanded={true} // Always show full content in expanded view
                          />
                        </div>
                      </Fade>
                    ))}
                  </Stack>
                </Collapse>
              )}
            </Stack>
          </Box>
        )}

        {initializing
          ? <LinearProgress />
          : signinRequired
            ? <SigninPrompt onSigninClick={handleSigninClick} />
            : <Stack width='100%' height='100%' spacing={2}>
              <Stack direction='row' spacing={1} alignItems='center' px={1}>
                {profilePicUrl ? (
                  <Box
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: theme => `2px solid ${theme.palette.secondary.light}`, // Add border
                    }}
                  >
                    <img
                      src={profilePicUrl}
                      alt="Profile"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </Box>
                ) : (
                  <Icon icon="hugeicons:ai-browser" width="24" height="24" />
                )}
                <Typography variant={profilePicUrl ? 'h4' : 'h6'}>{appName || 'Pixie'}</Typography>

                {/* Use the extracted CanvasPill component */}
                {!isChromeExtension && !canvasOpen && canvasCount > 0 && (
                  <CanvasPill
                    canvasCount={canvasCount}
                    onClick={handleOpenCanvas}
                  />
                )}
              </Stack>
              <Stack direction='row' width='100%' spacing={2} justifyContent='space-around' flexGrow={1} flexShrink={1} minHeight={0}>
                {isChromeExtension
                  ? <></>
                  : <CanvasView
                    sx={{ flexGrow: 1, flexShrink: 1 }}
                    debug={appRunOptions.debug}
                  />
                }
                <Box width={canvasOpen ? `min(${CHAT_VIEW_WIDTH}, 100%)` : '100%'} height='100%'>
                  <ChatView {...appRun} contentMaxWidth='768px' />
                </Box>
              </Stack>
            </Stack>
        }
      </Box>
    </AppPopover>
  </CustomizationProvider>
}
